import React, { useEffect, useState } from "react";
import clsx from "clsx";
import PropTypes from "prop-types";
import PhoneInput from "react-phone-input-2";
import { Box } from "@material-ui/core";
import i18next from "i18next";

import { useSelectedTheme } from "../../../Hooks";
import { CustomButton } from "../CustomButton";
import { DropdownIcon, PlusIcon } from "../../../assets/icons";

// // Styles
import useStyles from "./styles";

const PhoneNumberInput = ({
  isValid,
  country,
  onInputChanged,
  idRef,
  isRequired,
  isDisabled,
  value,
  wrapperClasses,
  inputClasses,
  labelClasses,
  labelValue,
  inputPlaceholder,
  onInputBlur,
  error,
  helperText,
  isSubmitted,
  themeClass,
  onKeyDown,
  onKeyUp,
  onPaste,
  Style = {},
  allowOthers,
  isAdding = false,
  setIsAdding,
  setNewValue,
  fieldClasses,
  // errorClasses,
  // buttonOptions,
  // isLoading,
  // afterIconClasses,
}) => {
  const styles = useStyles();

  const {
    theme: { palette },
  } = useSelectedTheme();
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState("+971"); // State to store input value

  const [isBlurOrChanged, setIsBlurOrChanged] = useState(false);
  const CustomizationMasks = {
    ci: "(...) ...-...-..-....",
    mx: "(...) ...-...-..",
    ar: "(..) ...-....-..",
    jo: "(..) ...-....",
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleAddPhone = () => {
    setNewValue((prev) => {
      const others = prev?.others || [];
      const normalizePhone = (phone) => phone?.replace(/\D/g, "");

      // Check if inputValue is already in others (format-insensitive)
      if (
        inputValue &&
        !others?.some(
          (phone) => normalizePhone(phone) === normalizePhone(inputValue)
        ) &&
        normalizePhone(inputValue) !== normalizePhone(value)
      ) {
        return {
          ...prev,
          others: [...(others || []), inputValue],
        };
      }

      return prev; // Return unchanged state if duplicate
    });

    setInputValue("+971"); // Clear the input field after adding
  };

  return (
    <Box
      style={{
        position: "relative",
        width:'100%'
      }}
      className={`${wrapperClasses} ${themeClass}`}
    >
      <Box className={styles.errorMessageContainer}>
        <PhoneInput
          style={{
            ...Style,
          }}
          onFocus={handleFocus}
          className={clsx(styles.phoneInputContainer, {[fieldClasses]: !!fieldClasses})} // Pass custom styles here
          specialLabel={false}
          isValid={isValid}
          country={country}
          masks={CustomizationMasks}
          excludeCountries={["JE", "je"]}
          enableLongNumbers
          inputProps={{
            required: isRequired,
            id: idRef || labelValue || inputClasses || labelClasses || "",
            autoComplete: "new-password",
            onKeyDown,
            onKeyUp,
            onPaste,
          }}
          containerClass={`${
            (helperText &&
              error &&
              (isSubmitted || isBlurOrChanged) &&
              " phone-error") ||
            ""
          }`}
          disabled={isDisabled}
          value={isAdding ? inputValue : value || ""}
          inputClass={`${styles.phoneInput} ${inputClasses} ${
            error ? styles.inputContainerError : ""
          } ${!error && isFocused ? styles.inputContainerFocused : ""}`}
          onBlur={(event) => {
            setIsFocused(false);
            setIsBlurOrChanged(true);
            setInputValue(event?.target?.value); // Set inputValue on blur
            if (onInputBlur) onInputBlur(event);
          }}
          onChange={(newValue, newCountry, event) => {
            setIsBlurOrChanged(true);
            if (newValue && newCountry?.dialCode?.includes(newValue)) {
              if (onInputChanged) onInputChanged(newValue, newCountry, event);
              return;
            } else if (newValue && !newValue.startsWith(newCountry.dialCode)) {
              if (onInputChanged)
                onInputChanged(
                  newCountry.dialCode + newValue,
                  newCountry,
                  event
                );
              return;
            }
            if (onInputChanged) onInputChanged(newValue, newCountry, event);
          }}
          enableTerritories
          enableSearch
          searchPlaceholder="Search"
          localization={i18next.language}
          placeholder={inputPlaceholder}
        />

        {error && <Box className={styles.errorMessage}>{error}</Box>}

        {allowOthers && (
          <Box className={styles.containerAddItem}>
            <CustomButton
              size="sm"
              variant="text"
              color="tertiaryGray"
              style={{ display: "flex", gap: "12px" }}
              disabled={!isValid}
              startIcon={
                <PlusIcon
                  width="20"
                  height="20"
                  fill={palette.foreground.quarterary}
                />
              }
              onClick={() => {
                setIsAdding(true);

                handleAddPhone();
              }}
            >
              Add another
            </CustomButton>
          </Box>
        )}
      </Box>

      <DropdownIcon
        className={styles.arrowStyle}
        width="20"
        height="20"
        fill={palette.foreground.quarterary}
      />
    </Box>
  );
};

export default PhoneNumberInput;

PhoneNumberInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.any]),
  onInputChanged: PropTypes.func,
  isValid: PropTypes.func,
  idRef: PropTypes.string.isRequired,
  wrapperClasses: PropTypes.string,
  className: PropTypes.string,
  inputClasses: PropTypes.string,
  labelClasses: PropTypes.string,
  translationPath: PropTypes.string,
  parentTranslationPath: PropTypes.string,
  labelValue: PropTypes.string,
  country: PropTypes.string,
  isRequired: PropTypes.bool,
  isDisabled: PropTypes.bool,
  errorClasses: PropTypes.string,
  inputPlaceholder: PropTypes.string,
  onInputBlur: PropTypes.func,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  isSubmitted: PropTypes.bool,
  isLoading: PropTypes.bool,
  themeClass: PropTypes.oneOf(["theme-default"]),
  afterIconClasses: PropTypes.string,
  buttonOptions: PropTypes.shape({
    className: PropTypes.string,
    iconClasses: PropTypes.string,
    onActionClicked: PropTypes.func,
    isDisabled: PropTypes.bool,
    isRequired: PropTypes.bool,
  }),
};
PhoneNumberInput.defaultProps = {
  className: "",
  value: undefined,
  onInputChanged: undefined,
  isValid: undefined,
  country: "ae",
  isRequired: false,
  isDisabled: false,
  translationPath: "",
  parentTranslationPath: "",
  labelValue: null,
  buttonOptions: null,
  wrapperClasses: "",
  inputClasses: "",
  labelClasses: "",
  errorClasses: "",
  themeClass: "theme-default",
  inputPlaceholder: undefined,
  onInputBlur: undefined,
  error: false,
  helperText: undefined,
  isSubmitted: false,
  isLoading: false,
  afterIconClasses: "",
};
